// frontend/src/pages/purchase/ProductDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import Select from 'react-select';
import { getProductDetail, addSingleVariant } from '../../utils/productApi';
import { getSuppliers } from '../../utils/supplierApi';
import { apiFetch } from '../../utils/api';

function ProductDetail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [masterOptions, setMasterOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [bulkEdit, setBulkEdit] = useState({ supplier_uuid: '', stock_quantity: '', default_cost_price: '', price_with_tax: '' });

  const [newVariant, setNewVariant] = useState({
    supplier_uuid: '',
    variant_option_1_uuid: '', variant_code_1: '',
    variant_option_2_uuid: '', variant_code_2: '',
    custom_sku_code: '', // 🌟 追加：手動確認・編集用のSKUコード
    default_cost_price: '', price_with_tax: '', jan_code: ''
  });

  const [selectedL1, setSelectedL1] = useState('');
  const [selectedL2, setSelectedL2] = useState('');
  const [selectedL3, setSelectedL3] = useState('');

  useEffect(() => {
    fetchProductDetails();
    fetchSuppliers();
    fetchCategories();
    fetchMasterOptions();
  }, [uuid]);

  useEffect(() => {
    if (product && allCategories.length > 0) {
      const currentCategoryUuid = product.internal_category_uuid || product.internal_category?.uuid;
      const cat = allCategories.find(c => c.uuid === currentCategoryUuid);
      if (cat) {
        if (cat.level === 3) {
          setSelectedL3(cat.uuid);
          setSelectedL2(cat.parent_uuid);
          const parent = allCategories.find(c => c.uuid === cat.parent_uuid);
          setSelectedL1(parent ? parent.parent_uuid : '');
        } else if (cat.level === 2) {
          setSelectedL3('');
          setSelectedL2(cat.uuid);
          setSelectedL1(cat.parent_uuid);
        } else if (cat.level === 1) {
          setSelectedL3('');
          setSelectedL2('');
          setSelectedL1(cat.uuid);
        }
      } else {
        setSelectedL1(''); setSelectedL2(''); setSelectedL3('');
      }
    }
  }, [product?.internal_category_uuid, product?.internal_category?.uuid, allCategories]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const data = await getProductDetail(uuid);
      setProduct(data);
      setVariants(data.variants || []);
    } catch (e) { alert('商品データのロードに失敗しました。'); }
    setLoading(false);
  };

  const fetchSuppliers = async () => {
    try { const data = await getSuppliers(); setSuppliers(data); } catch (e) { }
  };

  const fetchCategories = async () => {
    try {
      const res = await apiFetch('/categories');
      if (res.ok) setAllCategories(await res.json());
    } catch (e) { }
  };

  const fetchMasterOptions = async () => {
    try {
      const res = await apiFetch('/variant-options');
      if (res.ok) setMasterOptions(await res.json());
    } catch (e) { }
  };

  const supplierOptions = suppliers.map(s => ({ value: s.uuid, label: `[${s.supplier_code || '--'}] ${s.name}`, code: s.supplier_code || '01' }));
  const l1Options = allCategories.filter(c => c.level === 1).map(c => ({ value: c.uuid, label: `[${c.code}] ${c.name}` }));
  const l2Options = allCategories.filter(c => c.level === 2 && c.parent_uuid === selectedL1).map(c => ({ value: c.uuid, label: `[${c.code}] ${c.name}` }));
  const l3Options = allCategories.filter(c => c.level === 3 && c.parent_uuid === selectedL2).map(c => ({ value: c.uuid, label: `[${c.code}] ${c.name}` }));

  const handleL1Change = (opt) => {
    const val = opt ? opt.value : '';
    setSelectedL1(val); setSelectedL2(''); setSelectedL3('');
    setProduct(prev => ({ ...prev, internal_category_uuid: val || null }));
  };
  const handleL2Change = (opt) => {
    const val = opt ? opt.value : '';
    setSelectedL2(val); setSelectedL3('');
    setProduct(prev => ({ ...prev, internal_category_uuid: val || selectedL1 || null }));
  };
  const handleL3Change = (opt) => {
    const val = opt ? opt.value : '';
    setSelectedL3(val);
    setProduct(prev => ({ ...prev, internal_category_uuid: val || selectedL2 || selectedL1 || null }));
  };

  const currentCategoryUuid = selectedL3 || selectedL2 || selectedL1 || product?.internal_category_uuid || product?.internal_category?.uuid;
  const currentCategory = allCategories.find(c => c.uuid === currentCategoryUuid) || product?.internal_category || {};

  const catVar1 = currentCategory.variants?.find(v => v.display_order === 1) || { label: '第1属性', variant_type_uuid: '' };
  const catVar2 = currentCategory.variants?.find(v => v.display_order === 2) || { label: '第2属性', variant_type_uuid: '' };

  const opt1MasterOptions = masterOptions
    .filter(o => o.variant_type_uuid === catVar1.variant_type_uuid)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map(o => ({
      value: o.uuid, code: o.code,
      label: o.english_name ? `${o.name} (${o.english_name})` : o.name,
      name: o.name, color_code: o.color_code, english_name: o.english_name
    }));

  const opt2MasterOptions = masterOptions
    .filter(o => o.variant_type_uuid === catVar2.variant_type_uuid)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map(o => ({
      value: o.uuid, code: o.code,
      label: o.english_name ? `${o.name} (${o.english_name})` : o.name,
      name: o.name, color_code: o.color_code, english_name: o.english_name
    }));

  const masterOptionFormat = (opt) => (
    <div className="d-flex align-items-center">
      {opt.color_code && <div className="border rounded shadow-sm me-2" style={{ width: '16px', height: '18px', backgroundColor: opt.color_code }}></div>}
      <span>{opt.label}</span>
    </div>
  );

  const getSelectValue = (uuid, targetOptions) => {
    if (!uuid) return null;
    const found = targetOptions.find(o => o.value === uuid);
    if (found) return found;

    const fallback = masterOptions.find(o => o.uuid === uuid);
    if (fallback) {
      return {
        value: fallback.uuid, code: fallback.code,
        label: fallback.english_name ? `${fallback.name} (${fallback.english_name}) (保存済)` : `${fallback.name} (保存済)`,
        name: fallback.name, color_code: fallback.color_code
      };
    }
    return null;
  };

  const handleSaveProductMain = async () => {
    setSaving(true);
    try {
      const payload = {
        name: product.name, brand: product.brand, season: product.season, note: product.note,
        internal_category_uuid: product.internal_category_uuid || null
      };
      const res = await apiFetch(`/products/${uuid}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('保存に失敗しました');
      alert('商品情報およびカテゴリ紐付けを保存しました！');
      fetchProductDetails();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const handleApplyBulkEdit = () => {
    const updated = variants.map(v => {
      const newV = { ...v };
      if (bulkEdit.supplier_uuid) newV.supplier_uuid = bulkEdit.supplier_uuid;
      if (bulkEdit.stock_quantity !== '') newV.stock_quantity = bulkEdit.stock_quantity;
      if (bulkEdit.default_cost_price !== '') newV.default_cost_price = bulkEdit.default_cost_price;
      if (bulkEdit.price_with_tax !== '') newV.price_with_tax = bulkEdit.price_with_tax;
      return newV;
    });
    setVariants(updated);
    setBulkEdit({ supplier_uuid: '', stock_quantity: '', default_cost_price: '', price_with_tax: '' });
  };

  const handleBulkSaveVariants = async () => {
    try {
      const promises = variants.map(v => {
        return apiFetch(`/products/${uuid}/variants/${v.uuid}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            supplier_uuid: v.supplier_uuid || null, stock_quantity: parseInt(v.stock_quantity, 10) || 0,
            default_cost_price: parseInt(v.default_cost_price, 10) || 0, price_with_tax: parseInt(v.price_with_tax, 10) || 0,
            jan_code: v.jan_code || "", variant_option_1_uuid: v.variant_option_1_uuid, variant_option_2_uuid: v.variant_option_2_uuid
          })
        });
      });
      await Promise.all(promises); alert('すべてのSKUを一括保存しました！');
      fetchProductDetails();
    } catch (e) { alert('保存に失敗しました。'); }
  };

  // 🌟 追加：属性が選ばれたときに、テキストボックスの中にSKUコードを自動的にプレビュー・反映させる関数
  const handleNewVariantSelect = (field, opt) => {
    const updated = { ...newVariant, [field]: opt ? opt.value : '' };
    if (field === 'variant_option_1_uuid') updated.variant_code_1 = opt ? opt.code : '';
    if (field === 'variant_option_2_uuid') updated.variant_code_2 = opt ? opt.code : '';

    const opt1 = opt1MasterOptions.find(o => o.value === updated.variant_option_1_uuid);
    const opt2 = opt2MasterOptions.find(o => o.value === updated.variant_option_2_uuid);

    const part1 = opt1 ? (opt1.english_name || opt1.code) : "00";
    const part2 = opt2 ? (opt2.english_name || opt2.code) : "00";

    updated.custom_sku_code = `${product.model_code}-${part1}-${part2}`;
    setNewVariant(updated);
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();
    if (!newVariant.supplier_uuid) return alert('仕入先を選択してください。');

    const selectedSupplier = supplierOptions.find(o => o.value === newVariant.supplier_uuid);
    try {
      const payload = { ...newVariant, default_cost_price: parseInt(newVariant.default_cost_price, 10) || 0, price_with_tax: parseInt(newVariant.price_with_tax, 10) || 0 };
      await addSingleVariant(uuid, payload, selectedSupplier.code);
      // 送信完了時に custom_sku_code も綺麗に空っぽにする
      setNewVariant(prev => ({ ...prev, variant_option_1_uuid: '', variant_code_1: '', variant_option_2_uuid: '', variant_code_2: '', custom_sku_code: '', jan_code: '' }));
      alert('新しいSKUを追加登録しました！');
      fetchProductDetails();
    } catch (err) { alert(err.message); }
  };

  const handleVariantChange = (index, field, value) => {
    const updated = [...variants]; updated[index][field] = value;
    if (field === 'variant_option_1_uuid' || field === 'variant_option_2_uuid') {
      const opt1 = masterOptions.find(o => o.uuid === (field === 'variant_option_1_uuid' ? value : updated[index].variant_option_1_uuid));
      const opt2 = masterOptions.find(o => o.uuid === (field === 'variant_option_2_uuid' ? value : updated[index].variant_option_2_uuid));
      if (opt1) updated[index].variant_code_1 = opt1.code;
      if (opt2) updated[index].variant_code_2 = opt2.code;
    }
    setVariants(updated);
  };

  const handleUpdateVariant = async (index) => {
    const v = variants[index];
    try {
      await apiFetch(`/products/${uuid}/variants/${v.uuid}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_uuid: v.supplier_uuid || null, stock_quantity: parseInt(v.stock_quantity, 10) || 0,
          default_cost_price: parseInt(v.default_cost_price, 10) || 0, price_with_tax: parseInt(v.price_with_tax, 10) || 0,
          jan_code: v.jan_code || "", variant_option_1_uuid: v.variant_option_1_uuid, variant_option_2_uuid: v.variant_option_2_uuid
        })
      });
      alert(`更新しました！`);
      fetchProductDetails();
    } catch (e) { alert(e.message); }
  };

  const handleGenerateBarcode = async (index) => {
    try {
      const res = await apiFetch(`/products/${uuid}/variants/${variants[index].uuid}/generate-barcode`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const updated = [...variants]; updated[index].store_barcode_1 = data.store_barcode_1; setVariants(updated);
        alert('自社バーコードを発行しました！');
      } else { alert((await res.json()).detail); }
    } catch (e) { }
  };

  const tableSelectStyles = {
    control: (base) => ({ ...base, minHeight: '30px', height: '30px', borderColor: '#dee2e6', fontSize: '13px' }),
    indicatorsContainer: (base) => ({ ...base, height: '28px' }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' })
  };

  if (loading || !product) return <div className="p-5 text-center text-muted"><i className="fa fa-spinner fa-spin fa-2x mb-3"></i><br />商品データを読み込み中...</div>;

  return (
    <div className="pb-5">
      <div className="d-flex align-items-center mb-4">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item"><Link to="/purchase/products">商品マスター一覧</Link></li>
            <li className="breadcrumb-item active">商品詳細管理</li>
          </ol>
          <h2 className="page-header mb-0 fw-bold">{product.model_code} <small className="text-muted ms-2 fs-6">の管理</small></h2>
        </div>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-white fw-bold shadow-sm" onClick={() => navigate('/purchase/products')}><i className="fa fa-arrow-left me-1"></i> 戻る</button>
          <button className="btn btn-success fw-bold shadow-sm px-4" onClick={handleSaveProductMain} disabled={saving}>{saving ? <i className="fa fa-spinner fa-spin me-2"></i> : <i className="fa fa-save me-2"></i>} 基本情報・ジャンルを保存</button>
        </div>
      </div>

      <div className="row gx-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-0"><h5 className="mb-0 fw-bold"><i className="fa fa-info-circle text-primary me-2"></i>共通基本情報</h5></div>
            <div className="card-body">
              <div className="mb-4">
                <label className="form-label fw-bold">商品タイトル <span className="text-danger">*</span></label>
                <input type="text" className="form-control form-control-lg fs-15px" value={product.name || ''} onChange={(e) => setProduct({ ...product, name: e.target.value })} />
              </div>
              <div className="mb-2">
                <label className="form-label fw-bold">商品説明 (社内メモ / 仕様)</label>
                <div className="bg-white" style={{ borderRadius: '8px', overflow: 'hidden' }}><ReactQuill theme="snow" value={product.note || ''} onChange={(content) => setProduct({ ...product, note: content })} style={{ height: '180px', marginBottom: '45px' }} /></div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom pt-4 pb-3"><h5 className="mb-0 fw-bold d-flex align-items-center"><i className="fa fa-tags text-indigo me-2"></i>SKUバリエーション <span className="badge bg-light text-dark border ms-2 fs-6">{variants.length} 件</span></h5></div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-muted small">
                    <tr>
                      <th className="ps-3 text-nowrap" style={{ width: "220px" }}>{catVar1.label} / {catVar2.label} (マスター限定)</th>
                      <th style={{ width: "160px" }}>仕入先(カタログ)</th>
                      <th style={{ width: "110px" }}>JANコード</th>
                      <th style={{ width: "120px" }}>自社バーコード</th>
                      <th style={{ width: "80px" }}>在庫</th>
                      <th style={{ width: "90px" }}>原価(¥)</th>
                      <th style={{ width: "90px" }}>売価(¥)</th>
                      <th className="text-center" style={{ width: '80px' }}>操作</th>
                    </tr>

                    <tr className="table-warning border-bottom border-warning">
                      <td className="ps-3 align-middle fw-bold text-dark"><i className="fa fa-bolt text-warning me-1"></i>一括操作</td>
                      <td>
                        <Select options={supplierOptions} value={supplierOptions.find(o => o.value === bulkEdit.supplier_uuid) || null} onChange={(opt) => setBulkEdit({ ...bulkEdit, supplier_uuid: opt ? opt.value : '' })} placeholder="一括選択..." isClearable={true} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} />
                      </td>
                      <td></td>
                      <td></td>
                      <td><input type="number" className="form-control form-control-sm text-end fw-bold" value={bulkEdit.stock_quantity} onChange={e => setBulkEdit({ ...bulkEdit, stock_quantity: e.target.value })} placeholder="一括" /></td>
                      <td><input type="number" className="form-control form-control-sm text-end fw-bold" value={bulkEdit.default_cost_price} onChange={e => setBulkEdit({ ...bulkEdit, default_cost_price: e.target.value })} placeholder="一括" /></td>
                      <td><input type="number" className="form-control form-control-sm text-end fw-bold" value={bulkEdit.price_with_tax} onChange={e => setBulkEdit({ ...bulkEdit, price_with_tax: e.target.value })} placeholder="一括" /></td>
                      <td className="text-center pe-3">
                        <div className="d-flex gap-1 justify-content-center">
                          <button className="btn btn-sm btn-dark fw-bold shadow-sm px-2 text-nowrap" onClick={handleApplyBulkEdit}>↓ 反映</button>
                          <button className="btn btn-sm btn-success fw-bold shadow-sm px-2 text-nowrap" onClick={handleBulkSaveVariants}>保存</button>
                        </div>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.length === 0 ? (<tr><td colSpan="8" className="text-center py-5 text-muted">SKUがありません。下から追加してください。</td></tr>) : (
                      variants.map((v, index) => (
                        <tr key={v.uuid}>
                          <td className="ps-3 py-2">
                            <div className="mb-2" style={{ maxWidth: '190px' }}>
                              <Select
                                options={opt1MasterOptions}
                                value={getSelectValue(v.variant_option_1_uuid, opt1MasterOptions)}
                                onChange={(opt) => handleVariantChange(index, 'variant_option_1_uuid', opt ? opt.value : '')}
                                placeholder={`-- ${catVar1.label} --`}
                                formatOptionLabel={masterOptionFormat}
                                styles={tableSelectStyles}
                                menuPortalTarget={document.body}
                              />
                            </div>
                            <div style={{ maxWidth: '190px' }}>
                              <Select
                                options={opt2MasterOptions}
                                value={getSelectValue(v.variant_option_2_uuid, opt2MasterOptions)}
                                onChange={(opt) => handleVariantChange(index, 'variant_option_2_uuid', opt ? opt.value : '')}
                                placeholder={`-- ${catVar2.label} --`}
                                formatOptionLabel={masterOptionFormat}
                                styles={tableSelectStyles}
                                menuPortalTarget={document.body}
                              />
                            </div>
                            <div className="small text-muted font-monospace mt-2" style={{ fontSize: '11px' }}>
                              <i className="fa fa-tag me-1 opacity-5"></i>{v.sku_code}
                            </div>
                          </td>
                          <td><Select options={supplierOptions} value={supplierOptions.find(o => o.value === v.supplier_uuid) || null} onChange={(opt) => handleVariantChange(index, 'supplier_uuid', opt ? opt.value : '')} menuPortalTarget={document.body} styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} className="fw-bold text-start" /></td>
                          <td><input type="text" className="form-control form-control-sm font-monospace" placeholder="任意" value={v.jan_code || ''} onChange={(e) => handleVariantChange(index, 'jan_code', e.target.value)} /></td>
                          <td>{v.store_barcode_1 ? (<div className="font-monospace small text-muted"><i className="fa fa-barcode me-1"></i>{v.store_barcode_1}</div>) : (<button type="button" className="btn btn-sm btn-outline-primary fw-bold px-2 py-1 text-nowrap shadow-sm" onClick={() => handleGenerateBarcode(index)}><i className="fa fa-magic me-1"></i>発行</button>)}</td>
                          <td><input type="number" className="form-control form-control-sm text-end fw-bold text-indigo" value={v.stock_quantity || 0} onChange={(e) => handleVariantChange(index, 'stock_quantity', e.target.value)} /></td>
                          <td><input type="number" className="form-control form-control-sm text-end" value={v.default_cost_price} onChange={(e) => handleVariantChange(index, 'default_cost_price', e.target.value)} /></td>
                          <td><input type="number" className="form-control form-control-sm text-end fw-bold" value={v.price_with_tax} onChange={(e) => handleVariantChange(index, 'price_with_tax', e.target.value)} /></td>
                          <td className="text-center pe-3"><button className="btn btn-sm btn-outline-success fw-bold px-2 py-1 shadow-sm text-nowrap" onClick={() => handleUpdateVariant(index)}>更新</button></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm bg-light">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3"><i className="fa fa-plus-circle text-success me-2"></i>新しいSKUを追加する</h6>
              <form onSubmit={handleAddVariant}>
                <div className="row g-2 mb-3">
                  <div className="col-md-12">
                    <label className="form-label small text-muted mb-1">主たる仕入先 <span className="text-danger">*</span></label>
                    <Select options={supplierOptions} value={supplierOptions.find(o => o.value === newVariant.supplier_uuid) || null} onChange={(opt) => setNewVariant({ ...newVariant, supplier_uuid: opt ? opt.value : '' })} placeholder="-- 仕入先を選択 --" className="fw-bold" isClearable={true} />
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small text-muted mb-1">{catVar1.label} を選択 *</label>
                    <Select
                      options={opt1MasterOptions}
                      value={opt1MasterOptions.find(o => o.value === newVariant.variant_option_1_uuid) || null}
                      // 🌟 onChangeでhandleNewVariantSelectを呼び出してプレビューを生成する
                      onChange={(opt) => handleNewVariantSelect('variant_option_1_uuid', opt)}
                      placeholder={`-- ${catVar1.label}を選択 --`}
                      formatOptionLabel={masterOptionFormat}
                      isClearable={true}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted mb-1">{catVar2.label} を選択 *</label>
                    <Select
                      options={opt2MasterOptions}
                      value={opt2MasterOptions.find(o => o.value === newVariant.variant_option_2_uuid) || null}
                      onChange={(opt) => handleNewVariantSelect('variant_option_2_uuid', opt)}
                      placeholder={`-- ${catVar2.label}を選択 --`}
                      formatOptionLabel={masterOptionFormat}
                      isClearable={true}
                    />
                  </div>
                </div>

                <div className="row g-2 align-items-end">
                  {/* 🌟 ユーザー確認・手動上書き用のSKUコードボックス */}
                  <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">
                      SKUコード指定
                    </label>
                    <input
                      type="text"
                      className="form-control bg-white font-monospace text-primary fw-bold"
                      placeholder={`${product.model_code}-〇〇`}
                      value={newVariant.custom_sku_code}
                      onChange={e => setNewVariant({ ...newVariant, custom_sku_code: e.target.value })}
                    />
                  </div>

                  <div className="col-md-2"><label className="form-label small text-muted mb-1">JANコード</label><input type="text" className="form-control bg-white" placeholder="456..." value={newVariant.jan_code} onChange={e => setNewVariant({ ...newVariant, jan_code: e.target.value })} /></div>
                  <div className="col-md-2"><label className="form-label small text-muted mb-1">標準原価(¥)</label><input type="number" className="form-control bg-white" placeholder="1000" value={newVariant.default_cost_price} onChange={e => setNewVariant({ ...newVariant, default_cost_price: e.target.value })} /></div>
                  <div className="col-md-3"><label className="form-label small text-muted mb-1 text-danger">販売価格(税込) *</label><input type="number" className="form-control bg-white" placeholder="3980" value={newVariant.price_with_tax} onChange={e => setNewVariant({ ...newVariant, price_with_tax: e.target.value })} required /></div>
                  <div className="col-md-2"><button type="submit" className="btn btn-dark w-100 fw-bold shadow-sm py-2">追加登録</button></div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-2"><h6 className="mb-0 fw-bold">代表画像 (ECサイト連動)</h6></div>
            <div className="card-body pt-0">
              <div className="bg-light border rounded d-flex align-items-center justify-content-center overflow-hidden" style={{ aspectRatio: '1/1', width: '100%' }}>
                {product.image_url ? <img src={product.image_url} alt="Product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div className="text-center text-muted opacity-5"><i className="fa fa-image fa-3x mb-2"></i><p className="mb-0 small fw-bold">画像なし</p></div>}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4 border-top border-3 border-indigo">
            <div className="card-header bg-white border-bottom-0 pt-3 pb-2"><h6 className="mb-0 fw-bold"><i className="fa fa-shopping-cart text-indigo me-2"></i>各モール販売ページ管理</h6></div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush fw-bold">
                <Link to={`/sales/shopify/${product.uuid}`} className="list-group-item list-group-item-action d-flex align-items-center py-3"><div className="w-30px text-center fs-4 me-3 text-success"><i className="fab fa-shopify"></i></div><div className="flex-fill">Shopify <small className="d-block text-muted fw-normal">自社公式サイト</small></div><div><span className="badge bg-success bg-opacity-20 text-success rounded-pill px-3">出品中</span></div></Link>
                <Link to={`/sales/rakuten/${product.uuid}`} className="list-group-item list-group-item-action d-flex align-items-center py-3"><div className="w-30px text-center fs-4 me-3 text-danger"><i className="fa fa-registered"></i></div><div className="flex-fill">楽天市場 <small className="d-block text-muted fw-normal">商品ページ・SEO設定</small></div><div><span className="badge bg-danger bg-opacity-20 text-danger rounded-pill px-3">出品中</span></div></Link>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-2"><h6 className="mb-0 fw-bold">自社ジャンル・属性の紐付け</h6></div>
            <div className="card-body pt-0">
              <div className="mb-2">
                <label className="form-label text-muted small fw-bold mb-1">大分類 (Lv.1)</label>
                <Select options={l1Options} value={l1Options.find(o => o.value === selectedL1) || null} onChange={handleL1Change} placeholder="-- 大分類を選択 --" isClearable={true} className="fw-bold" />
              </div>
              <div className="mb-2">
                <label className="form-label text-muted small fw-bold mb-1">中分類 (Lv.2)</label>
                <Select options={l2Options} value={l2Options.find(o => o.value === selectedL2) || null} onChange={handleL2Change} placeholder="-- 中分類を選択 --" isClearable={true} className="fw-bold" isDisabled={!selectedL1} />
              </div>
              <div className="mb-3">
                <label className="form-label text-muted small fw-bold mb-1">小分類 (Lv.3)</label>
                <Select options={l3Options} value={l3Options.find(o => o.value === selectedL3) || null} onChange={handleL3Change} placeholder="-- 小分類を選択 --" isClearable={true} className="fw-bold" isDisabled={!selectedL2} />
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-2"><h6 className="mb-0 fw-bold">管理ステータス</h6></div>
            <div className="card-body pt-0">
              <div className="mb-3"><label className="form-label text-muted small fw-bold mb-1">自社型番 (モデルコード)</label><div className="p-2 bg-light border rounded font-monospace text-primary fw-bold">{product.model_code}</div></div>
              <div className="mb-0"><label className="form-label text-muted small fw-bold mb-1">自動採番シリアル (バーコード用)</label><div className="p-2 bg-light border rounded font-monospace text-dark fw-bold">{product.product_barcode_code || '未発行'}</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;